import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Search,
  Filter,
  Grid,
  List,
  MapPin,
  ArrowRight,
  ShieldCheck,
  Building2,
  RotateCcw,
  Home,
  DollarSign,
  Megaphone,
  Award,
  BadgeDollarSign,
  UserCheck,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { PropertyCard, formatPriceInr } from "../common/property";
import "./Properties.css";

const baseUrl = process.env.REACT_APP_BASE_URL;
const API_URL = `${baseUrl}/all/property/list/all/purpose`;

const FALLBACK_PROPERTIES = [
  {
    id: 101,
    title: "Luxury 4BHK Sky Villa with Panoramic City View",
    budget: 28500000,
    price: 28500000,
    type: "Villa",
    purpose: "Sale",
    address: "Whitefield Main Rd",
    location: "Whitefield",
    area: { name: "Whitefield" },
    city: { name: "Bangalore" },
    state: { name: "Karnataka" },
    structure: { bed_rooms: 4, bath_rooms: 4, built_up_area: "3,400" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: true,
    verified: true,
  },
  {
    id: 102,
    title: "Sea-Facing Ultra Modern 3BHK Penthouse",
    budget: 45000000,
    price: 45000000,
    type: "Flat",
    purpose: "Sale",
    address: "Worli Sea Face",
    location: "Worli",
    area: { name: "Worli" },
    city: { name: "Mumbai" },
    state: { name: "Maharashtra" },
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,200" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: true,
    verified: true,
  },
  {
    id: 103,
    title: "Contemporary 3BHK Luxury Garden House",
    budget: 16500000,
    price: 16500000,
    type: "House",
    purpose: "Rent",
    address: "Gachibowli Financial District",
    location: "Gachibowli",
    area: { name: "Gachibowli" },
    city: { name: "Hyderabad" },
    state: { name: "Telangana" },
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,500" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: false,
    verified: true,
  },
  {
    id: 104,
    title: "Prime Golf View 4BHK Premium Apartment",
    budget: 22000000,
    price: 22000000,
    type: "Flat",
    purpose: "Sale",
    address: "Golf Course Extension",
    location: "Golf Course Ext",
    area: { name: "Sector 54" },
    city: { name: "Delhi NCR" },
    state: { name: "Haryana" },
    structure: { bed_rooms: 4, bath_rooms: 4, built_up_area: "3,100" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: true,
    verified: true,
  },
  {
    id: 105,
    title: "Architectural 5BHK Independent Duplex Estate",
    budget: 52000000,
    price: 52000000,
    type: "Villa",
    purpose: "Sale",
    address: "Jubilee Hills Rd No 36",
    location: "Jubilee Hills",
    area: { name: "Jubilee Hills" },
    city: { name: "Hyderabad" },
    state: { name: "Telangana" },
    structure: { bed_rooms: 5, bath_rooms: 6, built_up_area: "5,800" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: true,
    verified: true,
  },
  {
    id: 106,
    title: "Modern 2BHK High-Rise Apartment with Balcony",
    budget: 7800000,
    price: 7800000,
    type: "Flat",
    purpose: "Rent",
    address: "Sector 62 Expressway",
    location: "Sector 62",
    area: { name: "Sector 62" },
    city: { name: "Noida" },
    state: { name: "Uttar Pradesh" },
    structure: { bed_rooms: 2, bath_rooms: 2, built_up_area: "1,250" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: false,
    verified: true,
  },
  {
    id: 107,
    title: "Eco Living Smart 3BHK Independent Villa",
    budget: 19500000,
    price: 19500000,
    type: "Villa",
    purpose: "Sale",
    address: "Kharadi IT Park Corridor",
    location: "Kharadi",
    area: { name: "Kharadi" },
    city: { name: "Pune" },
    state: { name: "Maharashtra" },
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,350" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: false,
    verified: true,
  },
  {
    id: 108,
    title: "Exclusive 4BHK Grand Family Villa with Lawn",
    budget: 34000000,
    price: 34000000,
    type: "House",
    purpose: "Sale",
    address: "Indiranagar 100ft Rd",
    location: "Indiranagar",
    area: { name: "Indiranagar" },
    city: { name: "Bangalore" },
    state: { name: "Karnataka" },
    structure: { bed_rooms: 4, bath_rooms: 4, built_up_area: "3,750" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    featured: true,
    verified: true,
  },
];

const PROPERTY_TYPES = ["All Types", "Villa", "Flat", "House", "Plot", "Commercial"];

const CITIES = [
  "All Cities",
  "Bangalore",
  "Mumbai",
  "Delhi NCR",
  "Noida",
  "Pune",
  "Hyderabad",
  "Ghaziabad",
];

const BUDGET_OPTIONS = [
  { label: "Any Budget", value: "" },
  { label: "Under ₹50 Lac", value: "0-5000000" },
  { label: "₹50 Lac - ₹1.5 Cr", value: "5000000-15000000" },
  { label: "₹1.5 Cr - ₹3 Cr", value: "15000000-30000000" },
  { label: "₹3 Cr & Above", value: "30000000+" },
];

const SORT_OPTIONS = [
  { label: "Featured First", value: "featured" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Newest First", value: "newest" },
];

const SERVICES = [
  {
    icon: Home,
    title: "Looking to Buy?",
    text: "Find your dream property with our verified listings, detailed floorplans, and direct contact.",
    btn: "Search Properties",
    action: "search",
  },
  {
    icon: DollarSign,
    title: "Want to Sell?",
    text: "List your property with zero brokerage and reach thousands of active buyers across India.",
    btn: "List Your Property",
    action: "sell",
  },
  {
    icon: Megaphone,
    title: "Want to Promote?",
    text: "Boost your property's visibility with our featured and premium highlight services.",
    btn: "Promote Now",
    action: "promote",
  },
];

const WHY_ITEMS = [
  {
    icon: Award,
    title: "100% Verified Properties",
    text: "Every property goes through strict documentation and authenticity checks.",
  },
  {
    icon: BadgeDollarSign,
    title: "Zero Hidden Brokerage",
    text: "Transparent pricing, direct builder and verified seller engagements.",
  },
  {
    icon: UserCheck,
    title: "Dedicated Real Estate Guides",
    text: "Professional real estate advisors to assist your site visits and paperwork.",
  },
  {
    icon: Clock,
    title: "Instant Support & Insights",
    text: "Round-the-clock customer assistance for scheduling visits and inquiries.",
  },
];

const PropertiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [assetUrl, setAssetUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'list'
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [savedProperties, setSavedProperties] = useState({});

  // Filter States initialized from URL search params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedPurpose, setSelectedPurpose] = useState(searchParams.get("purpose") || "All");
  const [selectedType, setSelectedType] = useState(searchParams.get("type") || "All Types");
  const [selectedCity, setSelectedCity] = useState(searchParams.get("city") || "All Cities");
  const [selectedBudget, setSelectedBudget] = useState(searchParams.get("budget") || "");
  const [selectedSort, setSelectedSort] = useState(searchParams.get("sort") || "featured");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  const syncUrlParams = useCallback(
    (newParams) => {
      const current = Object.fromEntries(searchParams.entries());
      const merged = { ...current, ...newParams };
      Object.keys(merged).forEach((k) => {
        if (!merged[k] || merged[k] === "All" || merged[k] === "All Types" || merged[k] === "All Cities") {
          delete merged[k];
        }
      });
      setSearchParams(merged);
    },
    [searchParams, setSearchParams]
  );

  const fetchPropertyData = useCallback(async () => {
    setLoading(true);
    try {
      const typesToFetch = ["Villa", "Flat", "House", "Plot"];
      const responses = await Promise.allSettled(
        typesToFetch.map(async (type) => {
          const res = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              page_number: 1,
              page_size: 10,
              order_by: "ASC",
              type,
            }),
          });
          if (!res.ok) throw new Error("Network response not ok");
          return await res.json();
        })
      );

      const validResponses = responses
        .filter((r) => r.status === "fulfilled" && r.value?.statusType === "SUCCESS")
        .map((r) => r.value);

      if (validResponses.length > 0) {
        const firstWithAsset = validResponses.find((d) => d.assetUrl);
        if (firstWithAsset) setAssetUrl(firstWithAsset.assetUrl);

        const allFetchedProps = validResponses.flatMap((d) => d.properties || []);
        if (allFetchedProps.length > 0) {
          // Harmonize property fields
          const harmonized = allFetchedProps.map((p) => ({
            ...p,
            price: p.budget || p.price || 0,
            type: typeof p.type === "object" ? p.type?.name : p.type || "Property",
            purpose: typeof p.purpose === "object" ? p.purpose?.name : p.purpose || "Sale",
            city: typeof p.city === "object" ? p.city : { name: p.city || "" },
            state: typeof p.state === "object" ? p.state : { name: p.state || "" },
            area: typeof p.area === "object" ? p.area : { name: p.area || p.address || "" },
          }));
          setProperties(harmonized);
        } else {
          setProperties(FALLBACK_PROPERTIES);
        }
      } else {
        setProperties(FALLBACK_PROPERTIES);
      }
    } catch (error) {
      console.warn("Using verified fallback properties:", error);
      setProperties(FALLBACK_PROPERTIES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPropertyData();
  }, [fetchPropertyData]);

  // Handle URL changes when user navigates with Back/Forward
  useEffect(() => {
    setSearchQuery(searchParams.get("search") || "");
    setSelectedPurpose(searchParams.get("purpose") || "All");
    setSelectedType(searchParams.get("type") || "All Types");
    setSelectedCity(searchParams.get("city") || "All Cities");
    setSelectedBudget(searchParams.get("budget") || "");
    setSelectedSort(searchParams.get("sort") || "featured");
  }, [searchParams]);

  const handlePurposeChange = (purpose) => {
    setSelectedPurpose(purpose);
    setCurrentPage(1);
    syncUrlParams({ purpose: purpose === "All" ? "" : purpose });
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setCurrentPage(1);
    syncUrlParams({ type: type === "All Types" ? "" : type });
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setCurrentPage(1);
    syncUrlParams({ city: city === "All Cities" ? "" : city });
  };

  const handleBudgetChange = (budget) => {
    setSelectedBudget(budget);
    setCurrentPage(1);
    syncUrlParams({ budget });
  };

  const handleSortChange = (sort) => {
    setSelectedSort(sort);
    syncUrlParams({ sort });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    syncUrlParams({ search: searchQuery.trim() });
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedPurpose("All");
    setSelectedType("All Types");
    setSelectedCity("All Cities");
    setSelectedBudget("");
    setSelectedSort("featured");
    setCurrentPage(1);
    setSearchParams({});
  };

  const toggleSaveProperty = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSavedProperties((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleServiceNav = (action) => {
    if (action === "sell" || action === "promote") {
      navigate("/partner/signup");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Filter and sort the loaded properties
  const filteredProperties = useMemo(() => {
    return properties.filter((item) => {
      // Purpose check
      if (selectedPurpose !== "All") {
        const pName = typeof item.purpose === "object" ? item.purpose?.name : item.purpose;
        if (pName && !pName.toLowerCase().includes(selectedPurpose.toLowerCase())) {
          return false;
        }
      }

      // Type check
      if (selectedType !== "All Types") {
        const tName = typeof item.type === "object" ? item.type?.name : item.type;
        if (tName && !tName.toLowerCase().includes(selectedType.toLowerCase())) {
          return false;
        }
      }

      // City check
      if (selectedCity !== "All Cities") {
        const cName = item.city?.name || item.location || item.address || "";
        if (!cName.toLowerCase().includes(selectedCity.toLowerCase())) {
          return false;
        }
      }

      // Budget check
      if (selectedBudget) {
        const cost = Number(item.budget || item.price || 0);
        if (selectedBudget === "0-5000000" && cost > 5000000) return false;
        if (selectedBudget === "5000000-15000000" && (cost < 5000000 || cost > 15000000)) return false;
        if (selectedBudget === "15000000-30000000" && (cost < 15000000 || cost > 30000000)) return false;
        if (selectedBudget === "30000000+" && cost < 30000000) return false;
      }

      // Search keyword check (title, address, area, city)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = (item.title || item.name || "").toLowerCase().includes(q);
        const addressMatch = (item.address || "").toLowerCase().includes(q);
        const areaMatch = (item.area?.name || "").toLowerCase().includes(q);
        const cityMatch = (item.city?.name || "").toLowerCase().includes(q);
        if (!titleMatch && !addressMatch && !areaMatch && !cityMatch) {
          return false;
        }
      }

      return true;
    });
  }, [properties, selectedPurpose, selectedType, selectedCity, selectedBudget, searchQuery]);

  const sortedProperties = useMemo(() => {
    const list = [...filteredProperties];
    if (selectedSort === "price_asc") {
      list.sort((a, b) => Number(a.budget || a.price || 0) - Number(b.budget || b.price || 0));
    } else if (selectedSort === "price_desc") {
      list.sort((a, b) => Number(b.budget || b.price || 0) - Number(a.budget || a.price || 0));
    } else if (selectedSort === "newest") {
      list.sort((a, b) => (b.id > a.id ? 1 : -1));
    }
    return list;
  }, [filteredProperties, selectedSort]);

  // Pagination calculation
  const totalPages = Math.ceil(sortedProperties.length / pageSize) || 1;
  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedProperties.slice(start, start + pageSize);
  }, [sortedProperties, currentPage, pageSize]);

  const activeFiltersCount = [
    selectedPurpose !== "All",
    selectedType !== "All Types",
    selectedCity !== "All Cities",
    !!selectedBudget,
    !!searchQuery,
  ].filter(Boolean).length;


  return (
    <main className="props-page" id="property-listing-top">
      {/* ─── Hero Header & Search Bar ────────────────────────────────────────── */}
      <section className="props-hero">
        <div className="props-hero__container">
          <div className="props-hero__header">
            <span className="props-hero__eyebrow">
              <ShieldCheck size={14} className="text-primary" />
              <span>Verified Real Estate Marketplace</span>
            </span>
            <h1 className="props-hero__title">
              Explore Available <em>Properties</em>
            </h1>
            <p className="props-hero__subtitle">
              Browse thousands of certified homes, luxury villas, modern apartments, and premium commercial listings across India.
            </p>
          </div>

          {/* Quick Purpose Switcher */}
          <div className="props-hero__tabs" role="tablist" aria-label="Property Purpose">
            {["All", "Buy", "Rent", "Commercial"].map((purpose) => (
              <button
                key={purpose}
                type="button"
                role="tab"
                aria-selected={selectedPurpose === purpose}
                className={`props-hero__tab ${selectedPurpose === purpose ? "props-hero__tab--active" : ""}`}
                onClick={() => handlePurposeChange(purpose)}
              >
                {purpose === "All" ? "All Properties" : `For ${purpose}`}
              </button>
            ))}
          </div>

          {/* Search & Filter Bar */}
          <form className="props-searchbar" onSubmit={handleSearchSubmit}>
            <div className="props-searchbar__field props-searchbar__field--search">
              <Search size={18} className="props-searchbar__icon" />
              <input
                type="text"
                className="props-searchbar__input"
                placeholder="Search by locality, project name, or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search properties"
              />
            </div>

            <div className="props-searchbar__field">
              <MapPin size={17} className="props-searchbar__icon" />
              <select
                className="props-searchbar__select"
                value={selectedCity}
                onChange={(e) => handleCityChange(e.target.value)}
                aria-label="Filter by city"
              >
                {CITIES.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div className="props-searchbar__field">
              <Home size={17} className="props-searchbar__icon" />
              <select
                className="props-searchbar__select"
                value={selectedType}
                onChange={(e) => handleTypeChange(e.target.value)}
                aria-label="Filter by property type"
              >
                {PROPERTY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="props-searchbar__field">
              <DollarSign size={17} className="props-searchbar__icon" />
              <select
                className="props-searchbar__select"
                value={selectedBudget}
                onChange={(e) => handleBudgetChange(e.target.value)}
                aria-label="Filter by budget"
              >
                {BUDGET_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit" className="props-searchbar__btn">
              <span>Find Homes</span>
              <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          </form>
        </div>
      </section>

      {/* ─── Main Content Section ────────────────────────────────────────────── */}
      <section className="props-main">
        <div className="props-page__container">
          {/* Controls Bar: Total counts, View Toggle, Sort */}
          <div className="props-controls">
            <div className="props-controls__left">
              <h2 className="props-controls__count">
                Showing <strong>{sortedProperties.length}</strong> {sortedProperties.length === 1 ? "Property" : "Properties"}
              </h2>
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  className="props-controls__reset-pill"
                  onClick={handleResetFilters}
                  aria-label="Reset all filters"
                >
                  <RotateCcw size={13} />
                  <span>Reset ({activeFiltersCount})</span>
                </button>
              )}
            </div>

            <div className="props-controls__right">
              <div className="props-controls__sort">
                <span className="props-controls__sort-label">Sort by:</span>
                <select
                  className="props-controls__sort-select"
                  value={selectedSort}
                  onChange={(e) => handleSortChange(e.target.value)}
                  aria-label="Sort properties"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="props-controls__views" role="group" aria-label="Layout view switch">
                <button
                  type="button"
                  className={`props-controls__view-btn ${viewMode === "grid" ? "props-controls__view-btn--active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <Grid size={16} />
                </button>
                <button
                  type="button"
                  className={`props-controls__view-btn ${viewMode === "list" ? "props-controls__view-btn--active" : ""}`}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                >
                  <List size={16} />
                </button>
              </div>

              <button
                type="button"
                className="props-controls__mobile-filter-btn"
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                aria-expanded={mobileFilterOpen}
                aria-label="Toggle mobile filters"
              >
                <Filter size={16} />
                <span>Filters {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ""}</span>
              </button>
            </div>
          </div>

          {/* Active Filter Chips */}
          {activeFiltersCount > 0 && (
            <div className="props-chips">
              <span className="props-chips__label">Active Filters:</span>
              {selectedPurpose !== "All" && (
                <span className="props-chip">
                  Purpose: {selectedPurpose}
                  <button type="button" onClick={() => handlePurposeChange("All")} aria-label="Remove purpose filter">
                    &times;
                  </button>
                </span>
              )}
              {selectedType !== "All Types" && (
                <span className="props-chip">
                  Type: {selectedType}
                  <button type="button" onClick={() => handleTypeChange("All Types")} aria-label="Remove type filter">
                    &times;
                  </button>
                </span>
              )}
              {selectedCity !== "All Cities" && (
                <span className="props-chip">
                  City: {selectedCity}
                  <button type="button" onClick={() => handleCityChange("All Cities")} aria-label="Remove city filter">
                    &times;
                  </button>
                </span>
              )}
              {selectedBudget && (
                <span className="props-chip">
                  Budget: {BUDGET_OPTIONS.find((b) => b.value === selectedBudget)?.label}
                  <button type="button" onClick={() => handleBudgetChange("")} aria-label="Remove budget filter">
                    &times;
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="props-chip">
                  "{searchQuery}"
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      syncUrlParams({ search: "" });
                    }}
                    aria-label="Remove search query"
                  >
                    &times;
                  </button>
                </span>
              )}
            </div>
          )}

          {/* ─── Grid / List Output ────────────────────────────────────────── */}
          {loading ? (
            <div className="props-skeleton-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="props-skeleton-card" aria-hidden="true">
                  <div className="props-skeleton-card__img" />
                  <div className="props-skeleton-card__body">
                    <div className="props-skeleton-card__line props-skeleton-card__line--short" />
                    <div className="props-skeleton-card__line props-skeleton-card__line--title" />
                    <div className="props-skeleton-card__line" />
                    <div className="props-skeleton-card__line props-skeleton-card__line--half" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginatedProperties.length === 0 ? (
            <div className="props-empty">
              <div className="props-empty__icon">
                <Building2 size={36} />
              </div>
              <h3 className="props-empty__title">No matching properties found</h3>
              <p className="props-empty__desc">
                We couldn't find any properties matching your current filter criteria. Try adjusting your search term, expanding your budget, or clearing some filters.
              </p>
              <button type="button" className="props-empty__btn" onClick={handleResetFilters}>
                <RotateCcw size={15} />
                <span>Reset All Filters</span>
              </button>
            </div>
          ) : (
            <div className={`props-grid ${viewMode === "list" ? "props-grid--list" : ""}`}>
              {paginatedProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  layout={viewMode}
                  isWishlisted={Boolean(savedProperties[property.id])}
                  onToggleWishlist={(e, id) => toggleSaveProperty(id, e)}
                  assetUrl={assetUrl}
                />
              ))}
            </div>
          )}

          {/* ─── Pagination ────────────────────────────────────────── */}
          {totalPages > 1 && (
            <nav className="props-pagination" aria-label="Properties pagination">
              <button
                type="button"
                className="props-pagination__btn"
                disabled={currentPage === 1}
                onClick={() => {
                  setCurrentPage((p) => Math.max(p - 1, 1));
                  window.scrollTo({ top: 400, behavior: "smooth" });
                }}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>

              <div className="props-pagination__pages">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`props-pagination__page-num ${currentPage === pageNum ? "props-pagination__page-num--active" : ""}`}
                      onClick={() => {
                        setCurrentPage(pageNum);
                        window.scrollTo({ top: 400, behavior: "smooth" });
                      }}
                      aria-current={currentPage === pageNum ? "page" : undefined}
                      aria-label={`Page ${pageNum}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="props-pagination__btn"
                disabled={currentPage === totalPages}
                onClick={() => {
                  setCurrentPage((p) => Math.min(p + 1, totalPages));
                  window.scrollTo({ top: 400, behavior: "smooth" });
                }}
                aria-label="Next page"
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </nav>
          )}
        </div>
      </section>

      {/* ─── Services Section ────────────────────────────────────────── */}
      <section className="props-page__section props-page__section--alt">
        <div className="props-page__container">
          <div className="props-page__section-header">
            <span className="props-page__section-label">All-in-One Real Estate</span>
            <h2 className="props-page__section-title">
              Our <em>Services</em>
            </h2>
          </div>
          <div className="props-page__services-grid">
            {SERVICES.map(({ icon: Icon, title, text, btn, action }) => (
              <div className="props-page__service-card" key={title}>
                <div className="props-page__service-icon">
                  <Icon size={22} />
                </div>
                <h3 className="props-page__service-title">{title}</h3>
                <p className="props-page__service-text">{text}</p>
                <button
                  type="button"
                  className="props-page__service-btn"
                  onClick={() => handleServiceNav(action)}
                >
                  {btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Choose Us Section ───────────────────────────────────── */}
      <section className="props-page__section">
        <div className="props-page__container">
          <div className="props-page__section-header">
            <span className="props-page__section-label">The All Home Desire Advantage</span>
            <h2 className="props-page__section-title">
              Why Choose <em>All Home Desire</em>
            </h2>
          </div>
          <div className="props-page__why-grid">
            {WHY_ITEMS.map(({ icon: Icon, title, text }, i) => (
              <div className="props-page__why-item" key={title}>
                <span className="props-page__why-number">0{i + 1}</span>
                <div className="props-page__why-icon">
                  <Icon size={20} />
                </div>
                <h4 className="props-page__why-title">{title}</h4>
                <p className="props-page__why-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default PropertiesPage;

